import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ManagedUser } from './types';

const humanize = (value: string) => value.replace(/[_-]+/g, ' ');
const formatPermissions = (permissions: string | string[]) =>
  Array.isArray(permissions) ? permissions.join(', ') : permissions;

/**
 * An account's organizations with permissions and sign-in method, and a warning when changing the
 * primary email also needs a change in an SSO identity provider.
 */
export function AccountOrganizations({ user }: { user: ManagedUser }) {
  const ssoOrgs = user.organizations.filter(org => org.isSso);

  return (
    <div className="space-y-2 px-4 py-3">
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        Organizations
      </div>
      {user.organizations.length === 0 && (
        <p className="text-sm text-muted-foreground">Not a member of any organization.</p>
      )}
      {user.organizations.map(org => (
        <div key={org.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1 text-sm">
          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{org.name}</span>
          <span className="text-muted-foreground">{formatPermissions(org.permissions)}</span>
          <Badge variant={org.isSso ? 'default' : 'outline'} className="capitalize">
            {humanize(org.signInMethod)}
          </Badge>
        </div>
      ))}
      {!user.isDeleted && ssoOrgs.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{ssoOrgs.map(org => org.name).join(', ')}</strong> signs in with SSO. After you
            change the primary email, their IT team must change it in their identity provider too,
            or the user&apos;s next SSO sign-in will create a second account.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
